import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import moment from "dayjs";
import Grid from "utils/Grid";
import { Typography, Button, Card } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ContentHeader from "../common/ContentHeader";
import TableGenerator from "../../../../../Common/TableGenerator";
import { ExportButton } from "../common/Buttons";
import Label from "../common/Label";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import styles from "./styles";
import _ from "lodash";
import _flatten from "lodash/flatten";
import { useSelector } from "react-redux";
import useProductSettings from "utils/useProductSettings";
import { handleServerErrors } from "helpers";
import { MSeriesModulesDropDown } from "../../../../../Common";

const InventoryDetails = () => {
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [modulesSelected, setModulesSelected] = useState([]);
  const current_user = useSelector((state) => state.userReducer.current_user);
  const productSettingsData = useProductSettings();
  const metrcEnabled = productSettingsData?.enable_metrc_tag ?? false;
  const [inventoryMap, setInventoryMap] = useState([]);
  const [loader, setLoader] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [mSeriesModules, setMSeriesModules] = useState([]);
  const [mSeriesSelectedModule, setMSeriesSelectedModule] = useState(null);
  const [inventoriesList, setInventoriesList] = useState([]);
  const [planogramsList, setPlanogramsList] = useState([]);
  const [deviceId] = useState(location?.state?.oro_id);
  const [devicePk, setDevicePk] = useState(null);
  const [saving, setSaving] = useState(false);
  // Device-level restock status: null | 'in_progress' | 'success' | 'failed'
  const [restockStatus, setRestockStatus] = useState(null);
  // Metrc tags staged from an imported file (keyed by rackKey), pending sync.
  // null = showing live inventory; object = showing file-loaded data.
  const [pendingMetrc, setPendingMetrc] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  // Mirror of pendingMetrc read synchronously by the (async) merge functions,
  // so a reload after discard/sync never re-applies stale staged values.
  const pendingMetrcRef = useRef(null);
  const fileInputRef = useRef(null);

  const setStagedMetrc = (map) => {
    pendingMetrcRef.current = map;
    setPendingMetrc(map);
  };

  const restockStatusMap = {
    success: { text: "Success", color: "success" },
    in_progress: { text: "Update in progress", color: "warning" },
    failed: { text: "Update failed", color: "error" },
  };

  const restockStatusPillColors = {
    success: "#2e7d32",
    in_progress: "#b25e00",
    failed: "#c62828",
  };

  // Solid white pill for use on the orange header band (colored chip is
  // invisible against the primary background).
  const renderRestockStatusPill = () => {
    if (!restockStatus) return null;
    const s = restockStatusMap[restockStatus];
    return (
      <span
        style={{
          background: "#fff",
          color: restockStatusPillColors[restockStatus],
          borderRadius: 12,
          padding: "3px 10px",
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: "nowrap",
          lineHeight: 1.6,
        }}
      >
        {s.text}
      </span>
    );
  };

  const navigate = useNavigate();
  const classes = styles();
  const theme = useTheme();

  const fields = [
    {
      key: "rack_number",
      columnName: "Rack Number",
      render: (_, val) => `${val.row} - ${val.col}`,
      type: "text",
      visible: true,
      disableSorting: true,
    },

    // {
    //   key: "cabinet_number",
    //   columnName: "Cabinet Number",
    //   type: "text",
    //   visible: true,
    // },
    {
      key: "product_id",
      columnName: "Product Id",
      render: (_, val) => val.product_id || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },
    {
      key: "product_sku",
      columnName: "Product SKU",
      render: (_, val) => val.product_sku || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },
    {
      key: "product_name",
      columnName: "Product Name",
      render: (_, val) => val.product_name || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },

    {
      key: "stock_capacity",
      columnName: "Stock/Capacity",
      render: (_, val) => `${val.stock} / ${val.capacity}`,
      type: "text",
      visible: true,
      disableSorting: true,
    },
    {
      key: "metrc_tag_id",
      columnName: "Metrc Tag",
      render: (_, val) =>
        val._fromFile ? (
          <span
            title="Loaded from file — not yet synced"
            style={{ color: theme.palette.primary.main }}
          >
            {val.metrc_tag_id || "—"}
          </span>
        ) : (
          val.metrc_tag_id || "---"
        ),
      type: "text",
      visible: metrcEnabled,
      disableSorting: true,
    },
    {
      key: "inventory",
      columnName: "Sold-out Status",
      type: "text",
      visible: true,
      render: (_, value) => (
        <>
          {getInventoryLabel(
            value.stock,
            value.capacity,
            false,
            value?.isException
          )}
        </>
      ),
      disableSorting: true,
    },
    {
      key: "last_stock",
      columnName: "Items Sold Since Last Restock",
      type: "text",
      visible: true,
      render: (_, value) =>
        value.last_stock ? value.last_stock - value.stock : "---",
      disableSorting: true,
    },
    {
      key: "shelf_life",
      columnName: "Shelf Life (Days)",
      render: (_, val) => val.shelf_life || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },
  ];

  const getInventoryLabel = (
    inventory,
    capacity,
    label,
    isException = false
  ) => {
    const stockPercentage = (100 * inventory) / capacity;
    let inventoryType = undefined;
    if (isException) {
      inventoryType = "exception";
    } else if (stockPercentage == 0 || capacity == 0) {
      inventoryType = "out_of_stock";
    } else if (stockPercentage <= 30) {
      inventoryType = "limited";
    } else {
      inventoryType = "in_stock";
    }
    const map = {
      in_stock: {
        text: "In Stock",
        color: "success",
      },
      out_of_stock: {
        text: "Out of Stock",
        color: "error",
      },
      limited: {
        text: "Limited",
        color: "warning",
      },
      exception: {
        text: "Exception",
        color: "error",
      },
    };

    const { text, color } = map[inventoryType];

    if (label) {
      return text;
    } else {
      return <Label color={color}>{text}</Label>;
    }
  };

  const setup = () => {
    setLoader(true);
    setInventoryMap([]);
    setModulesSelected([]);
  };

  const handleRes = (property = []) => {
    const inventory_data = JSON.parse((property[0] || {}).inventoryDetails);

    // setInventoryMap(_.map(inventory_data, (x) => ({...x, rack_number: `${x.row}-${x.col}`})) || []);
    // setProductDetails(_.map(inventory_data, (x) => ({...x, rack_number: `${x.row}-${x.col}`})) || []);
    // setNextPage(data.next);
    // setPreviousPage(data.previous);
    // setFirstPage(data.first);
    // setLastPage(data.last);
    setLoader(false);
  };

  const changePage = (url) => {
    setup();
    window.axiosIns
      .get(url)
      .then(({ data = {} }) => {
        handleRes(data.data);
      })
      .catch((err) => {
        setLoader(false);
        if (err.detail) {
          enqueueSnackbar(err.detail);
        } else {
          //   handleServerErrors(
          //     err,
          //     enqueueSnackbar,
          //     "Could not get device types. Try again."
          //   );
        }
      });
  };

  const getInventoryDetailsFromServer = async (
    module_count = 1,
    selectedModule
  ) => {
    const { data: vmExceptions } = await window.axiosIns.get(`vm_exceptions`, {
      params: {
        oro_id: deviceId,
        module_number: selectedModule || null,
        _scope: "AND",
        all: true,
      },
    });
    const exceptionsList = vmExceptions?.results || [];

    const { data: inventoryData } = await window.axiosIns(
      `/device-data/${deviceId}/com.digitalmediavending.Inventory/?limit=${module_count}`
    );
    const inventoriesData = inventoryData?.data?.property || [];
    setInventoriesList(inventoriesData);

    let inventory_details;
    if (selectedModule) {
      const moduleInventory =
        inventoriesData.find((item) => item.moduleNumber === selectedModule)
          ?.inventoryDetails || "[]";
      inventory_details = JSON.parse(moduleInventory);
    } else {
      inventory_details = JSON.parse(
        inventoriesData[0]?.inventoryDetails || "[]"
      );
    }

    const { data: planogramData } = await window.axiosIns.get("/planogram", {
      params: {
        cabinet_id__vm_id__oro_id: deviceId,
        peer_device: true,
      },
    });
    const planogramsList =
      planogramData?.results?.map((e) => {
        if ("is_disabled" in e) {
          return e;
        } else {
          return { ...e, is_disabled: false };
        }
      }) || [];
    setPlanogramsList(planogramsList);

    let planogram_details;

    if (selectedModule) {
      planogram_details =
        planogramsList.find((item) => item.module_number === selectedModule)
          ?.details || [];
    } else {
      planogram_details = planogramsList?.[0]?.details || [];
    }

    _.forEach(inventory_details, ({ col, row, stock, last_stock, metrc_tag_id }) => {
      const index = _.findIndex(
        planogram_details,
        (x) => x.col == col && x.row == row
      );

      if (index > -1) {
        const products = planogramsList?.[0]?.product_details || [];
        const prd = _.find(
          products,
          (x) => x.id === planogram_details[index]?.product_id
        );
        const product_sku = prd?.product_sku;
        const shelf_life = prd?.shelf_life;
        planogram_details[index] = {
          ...planogram_details[index],
          stock,
          last_stock,
          shelf_life,
          product_sku,
          metrc_tag_id: metrc_tag_id ?? planogram_details[index]?.metrc_tag_id ?? "",
        };
      }

      const isException =
        _.find(exceptionsList, (val) => val.row == row && val.col == col) ||
        false;
      planogram_details[index] = { ...planogram_details[index], isException };
    });

    if (!selectedModule && Array.isArray(planogram_details)) {
      planogram_details.forEach(({ row, col }, index) => {
        const isException =
          _.find(exceptionsList, (val) => val.row == row && val.col == col) ||
          false;
        planogram_details[index] = { ...planogram_details[index], isException };
      });
    }

    setInventoryMap(
      applyMetrcOverlays(withRowIds(
          stampModule(
            planogram_details,
            selectedModule ?? planogramsList?.[0]?.module_number
          )
        ))
    );
    setLoader(false);
  };

  const getInventoryDetails = async (selectedModule) => {
    setInventoryMap([]);
    setLoader(true);
    const { data: vmExceptions } = await window.axiosIns.get(`vm_exceptions`, {
      params: {
        oro_id: deviceId,
        module_number: selectedModule || null,
        _scope: "AND",
        all: true,
      },
    });
    setLoader(false);
    const exceptionsList = vmExceptions?.results || [];

    const moduleInventory =
      inventoriesList.find((item) => item.moduleNumber === selectedModule)
        ?.inventoryDetails || "[]";
    const inventory_details = JSON.parse(moduleInventory);

    const modulePlanogram = planogramsList.find(
      (item) => item.module_number === selectedModule
    );
    const planogram_details = modulePlanogram?.details || [];

    _.forEach(inventory_details, ({ col, row, stock, last_stock, metrc_tag_id }) => {
      const index = _.findIndex(
        planogram_details,
        (x) => x.col == col && x.row == row
      );

      if (index > -1) {
        const products = modulePlanogram?.product_details || [];
        const prd = _.find(
          products,
          (x) => x.id === planogram_details[index]?.product_id
        );
        const product_sku = prd?.product_sku;
        const shelf_life = prd?.shelf_life;
        planogram_details[index] = {
          ...planogram_details[index],
          stock,
          last_stock,
          shelf_life,
          product_sku,
          metrc_tag_id: metrc_tag_id ?? planogram_details[index]?.metrc_tag_id ?? "",
        };
      }

      const isException =
        _.find(exceptionsList, (val) => val.row == row && val.col == col) ||
        false;
      planogram_details[index] = { ...planogram_details[index], isException };
    });

    setInventoryMap(
      applyMetrcOverlays(withRowIds(stampModule(planogram_details, selectedModule)))
    );
  };

  const handleSort = (order) => {
    let list = _.cloneDeep(inventoryMap);
    const sortorder = order?.split("-")?.length > 1 ? "asc" : "desc";
    let value =
      order?.split("-")?.length > 1
        ? order?.split("-")[1]
        : order?.split("-")[0];
    if (value === "stock_capacity" || value === "inventory") {
      value = "stock";
    }
    list = _.orderBy(
      list,
      [
        (item) =>
          isNaN(item[value]) ? item[value]?.toLowerCase() : item[value],
      ],
      [sortorder]
    );
    setInventoryMap(list);
  };

  useEffect(() => {
    getDevice();
  }, []);

  const getDevice = async () => {
    try {
      setLoader(true);
      const { data } = await window.axiosIns(`/device`, {
        params: { oro_id: deviceId },
      });
      const deviceData = data?.data?.results[0] || {};
      setDevicePk(deviceData?.id || null);
      setRestockStatus(deviceData?.inventory_sync_status || "success");
      if (deviceData?.is_m_series) {
        let modulesObject = data.data.results[0].modules.map((item, index) => {
          return { label: `Module ${index + 1}`, value: item };
        });
        setMSeriesModules(modulesObject);
        setMSeriesSelectedModule(deviceData.modules[0]);
        getInventoryDetailsFromServer(
          deviceData.modules.length,
          deviceData.modules[0]
        );
      } else {
        getInventoryDetailsFromServer();
      }
    } catch (err) {
      console.log(err);
      setLoader(false);
    }
  };

  const onModuleChange = (val) => {
    setMSeriesSelectedModule(val.value);
    getInventoryDetails(val.value);
  };

  // Full inventory across ALL modules (m-series included), from the backend —
  // the source of truth for Export and Import so every module is covered.
  const fetchFullInventory = async () => {
    const { data } = await window.axiosIns.get(
      "inventory/device_full_inventory",
      { params: { device_id: devicePk } }
    );
    return data?.data?.rows || [];
  };

  const handleExport = async () => {
    if (!devicePk) {
      enqueueSnackbar("Device could not be resolved. Please refresh.");
      return;
    }
    setLoader(true);
    let fullRows = [];
    try {
      fullRows = await fetchFullInventory();
    } catch (err) {
      setLoader(false);
      handleServerErrors(err, enqueueSnackbar, "Could not fetch inventory.");
      return;
    }

    window.axiosIns
      .post(
        "/export-to-excel",
        {
          data: _.map(fullRows, (x) => ({
            Row: x.row,
            Column: x.col,
            Module: x.module_number ?? "",
            "Product Id": x.product_id || "---",
            "Product SKU": x.product_sku || "---",
            "Product Name": x.product_name || "---",
            Stock: x.stock || "0",
            "Sold-out Status": getInventoryLabel(x.stock, x.capacity, true, false),
            Capacity: x.capacity || "---",
            "Items Sold Since Last Restock": x.last_stock
              ? x.last_stock - x.stock
              : "---",
            "Shelf Life": x.shelf_life || "---",
            "Metrc Tag": x.metrc_tag_id || "",
          })),
          header_map: {
            Row: "Row",
            Column: "Column",
            Module: "Module",
            "Product Id": "Product ID",
            "Product SKU": "Product SKU",
            "Product Name": "Product Name",
            Stock: "Stock",
            "Sold-out Status": "Sold-out Status",
            Capacity: "Capacity",
            "Items Sold Since Last Restock": "Items Sold Since Last Restock",
            "Shelf Life": "Shelf Life (Days)",
            "Metrc Tag": "Metrc Tag",
          },
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        setLoader(false);
        enqueueSnackbar("Successfully exported");

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Inventory Per VM-Summary-${moment().format("MM-DD-YYYY")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setLoader(false);
        if (err.detail) {
          enqueueSnackbar(err.detail);
        } else {
          handleServerErrors(
            err,
            enqueueSnackbar,
            "Could not export to excel. Try again."
          );
        }
      });
  };

  // Stable per-rack id so table rows keep DOM identity across edits
  // (TableGenerator keys rows by record.id; without it inputs remount and lose focus).
  const withRowIds = (rows = []) =>
    rows.map((r) => ({
      ...r,
      id: r.id ?? `${r.row}-${r.col}-${r.module_number ?? ""}`,
    }));

  const pollRestockStatus = async (mobileRestockId, attempt = 0) => {
    try {
      const { data } = await window.axiosIns.get(
        "inventory/get_mobile_restock_status",
        { params: { mobile_restock_id: mobileRestockId } }
      );
      const status = data?.data?.restock_status;
      if (status === "success") {
        enqueueSnackbar("Inventory update applied on the device");
        setRestockStatus("success");
        setSaving(false);
        getDevice();
        return;
      }
      if (status === "failed") {
        enqueueSnackbar("Device failed to apply the inventory update");
        setRestockStatus("failed");
        setSaving(false);
        return;
      }
      if (attempt >= 20) {
        enqueueSnackbar("Update sent — device is still processing");
        setSaving(false);
        return;
      }
      setTimeout(() => pollRestockStatus(mobileRestockId, attempt + 1), 3000);
    } catch (err) {
      setSaving(false);
      handleServerErrors(
        err,
        enqueueSnackbar,
        "Could not fetch restock status."
      );
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  // Normalizes a rack key so parsed-file values and API values match even when
  // Excel round-trips numbers as floats (e.g. module "1" vs "1.0") — important
  // for m-series matching across modules.
  const rackKey = ({ row, col, module_number }) => {
    const norm = (v) => {
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      return Number.isFinite(n) ? String(n) : String(v).trim();
    };
    return `${norm(row)}-${norm(col)}-${norm(module_number)}`;
  };

  // Planogram rack rows often don't carry module_number, but device_full_inventory
  // (and the imported file) key racks by the cabinet's module. Stamp the current
  // module onto display rows so their rackKey matches the staged/file keys.
  const stampModule = (rows = [], mod) =>
    rows.map((r) => ({
      ...r,
      module_number:
        r.module_number === undefined ||
        r.module_number === null ||
        r.module_number === ""
          ? mod ?? ""
          : r.module_number,
    }));

  // Overlays staged (imported this session) metrc tags onto rows, highlighting
  // changed cells via _fromFile. Cleared on Sync/Discard.
  const applyMetrcOverlays = (rows = []) => {
    const pending = pendingMetrcRef.current;
    if (!pending) return rows;
    return rows.map((r) => {
      const key = rackKey(r);
      if (!(key in pending)) return r;
      const val = pending[key] ?? "";
      return { ...r, metrc_tag_id: val, _fromFile: val !== (r.metrc_tag_id ?? "") };
    });
  };

  // Import = load the file's metrc tags into the on-screen table (staged).
  // Nothing is sent to the device until the user clicks "Sync to device".
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSaving(true);
    setImportErrors([]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: parsed } = await window.axiosIns.post(
        "inventory/parse_inventory_file",
        formData
      );
      const parsedRows = parsed?.data?.rows || [];
      if (!parsedRows.length) {
        setSaving(false);
        setImportErrors(["The file has no inventory rows to import."]);
        return;
      }

      // Validate against THIS device: cabinet structure (racks + products).
      // Collect per-record errors for display.
      const fullRows = await fetchFullInventory();
      const deviceByRack = {};
      fullRows.forEach((r) => {
        deviceByRack[rackKey(r)] = r;
      });
      const fileKeys = new Set(parsedRows.map((r) => rackKey(r)));
      const errors = [];
      const label = (r) =>
        `Rack ${r.row}-${r.col}${r.module_number ? ` (module ${r.module_number})` : ""}`;

      // Structure: unexpected racks + product mismatches.
      parsedRows.forEach((pr) => {
        const dev = deviceByRack[rackKey(pr)];
        if (!dev) {
          errors.push(`${label(pr)} does not exist on this VM`);
          return;
        }
        if (
          pr.product_id != null &&
          dev.product_id != null &&
          String(pr.product_id) !== String(dev.product_id)
        ) {
          errors.push(
            `${label(pr)}: product ${pr.product_id} does not match VM product ${dev.product_id}`
          );
        }
      });
      // Missing racks (on device, not in file).
      const missing = fullRows.filter((r) => !fileKeys.has(rackKey(r)));
      if (missing.length) {
        errors.push(
          `${missing.length} VM rack(s) missing from the file (must include every rack).`
        );
      }

      if (errors.length) {
        setSaving(false);
        // Cap the list so a wildly wrong file doesn't flood the UI.
        const shown = errors.slice(0, 12);
        if (errors.length > shown.length)
          shown.push(`…and ${errors.length - shown.length} more issue(s).`);
        setImportErrors(shown);
        enqueueSnackbar("Import cancelled — file did not pass validation.");
        return;
      }

      // Map keyed by row-col-module so we only touch metrc tag, never stock.
      // Ignore tags on racks that hold no product.
      const metrcByRack = {};
      parsedRows.forEach((r) => {
        const key = rackKey(r);
        const dev = deviceByRack[key];
        if (!dev || dev.product_id == null) return; // no product → ignore tag
        metrcByRack[key] = r.metrc_tag_id ?? "";
      });
      setStagedMetrc(metrcByRack);
      // Load the file values into the currently displayed table.
      setInventoryMap((prev) => applyMetrcOverlays(prev));
      setSaving(false);
      enqueueSnackbar("Metrc tags loaded from file — review, then Save / Sync");
    } catch (err) {
      setSaving(false);
      handleServerErrors(
        err,
        enqueueSnackbar,
        "Could not read the file. Please upload a valid inventory export."
      );
    }
  };

  // Voluntary sync: push the staged (file-loaded) metrc tags to the device via
  // the mobile-restock flow. Stock is left as-is across all modules.
  const handleSyncToAndroid = async () => {
    if (!pendingMetrc) return;
    if (!devicePk) {
      enqueueSnackbar("Device could not be resolved. Please refresh.");
      return;
    }
    setSaving(true);
    try {
      const fullRows = await fetchFullInventory();
      // One batch id shared across all modules — the backend groups the
      // per-module restock records under this parentId.
      const parentId = String(Date.now());
      // Build the Android restock payload: array-of-modules, each an array of
      // rack objects (metrc overridden from the file, stock left as-is).
      const groups = new Map();
      fullRows.forEach((x) => {
        const key = rackKey(x);
        const metrc_tag_id =
          pendingMetrc && key in pendingMetrc
            ? pendingMetrc[key]
            : x.metrc_tag_id || ""; // fullRows already include cloud-saved tags
        const rack = {
          parentId,
          capacity: x.capacity,
          col: x.col,
          is_disabled: x.is_disabled || false,
          last_stock: x.last_stock ?? 0,
          metrc_tag_id,
          module_number: x.module_number ?? "",
          price: x.price ?? "",
          product_id: x.product_id != null ? String(x.product_id) : "0",
          row: x.row,
          stock: Number(x.stock) || 0, // stock is intentionally left as-is
          originalIndex: x.original_index ?? 0,
          product_name: x.product_name ?? "",
          product_picture: x.product_picture ?? "",
          expected_restock_value: x.expected_restock_value ?? 0,
          shelf_life: x.shelf_life ?? "",
          inventory_id: x.inventory_id ?? null,
        };
        const groupKey = x.inventory_id ?? "__none__";
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(rack);
      });
      const restock_details = Array.from(groups.values()).map((racks) =>
        _.sortBy(racks, "originalIndex")
      );
      const { data } = await window.axiosIns.post(
        "inventory/perform_mobile_restock",
        // restock_details is uploaded verbatim to S3, so it must be a JSON string.
        // source 'remote' marks this as a cloud restock (not an on-site mobile one).
        {
          device_id: devicePk,
          restock_details: JSON.stringify(restock_details),
          source: "remote",
        }
      );
      const mobileRestockId = data?.data?.mobile_restock_id;
      if (mobileRestockId) {
        enqueueSnackbar("Metrc tags sent to the device");
        setStagedMetrc(null);
        setRestockStatus("in_progress");
        pollRestockStatus(mobileRestockId);
      } else {
        setSaving(false);
        enqueueSnackbar("Something went wrong while sending the update");
      }
    } catch (err) {
      setSaving(false);
      handleServerErrors(err, enqueueSnackbar, "Could not sync to the device.");
    }
  };

  // Persist the staged tags to the cloud (Inventory.metrc_tags), independent of
  // the device. Sync (device push) remains a separate action.
  const handleDiscardImport = () => {
    setStagedMetrc(null);
    setImportErrors([]);
    getDevice(); // reload live inventory
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <ContentHeader title="" />
      <Card
        style={{ margin: "0px 20px 20px 20px" }}
        variant="contained"
        className={classes.headerToolbar}
      >
        <Grid style={{ padding: "10px 20px" }} container>
          <Grid
            
            xs={12}
            sm={12}
            md={3}
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <Typography variant="subtitle2">
              <strong>VM Serial : </strong>
              {location?.state?.serial_number || ""}
            </Typography>
          </Grid>
          <Grid
            
            xs={12}
            sm={12}
            md={3}
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <Typography variant="subtitle2">
              <strong>VM Name : </strong>
              {location?.state?.vm_name || ""}
            </Typography>
          </Grid>
          {metrcEnabled && restockStatus && (
            <Grid
              xs={12}
              sm={12}
              md={3}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <Typography variant="subtitle2" style={{ marginRight: 8 }}>
                <strong>Restock Status :</strong>
              </Typography>
              {renderRestockStatusPill()}
            </Grid>
          )}
          {mSeriesSelectedModule && (
            <Grid
              
              xs={12}
              sm={12}
              md={3}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <Typography variant="subtitle2" style={{ marginRight: 5 }}>
                <strong>Module :</strong>
              </Typography>
              <MSeriesModulesDropDown
                onChange={(val) => onModuleChange(val)}
                value={mSeriesSelectedModule}
                defaultValue={mSeriesModules[0]}
                options={mSeriesModules}
                size="small"
                backgroundColor="white"
              />
            </Grid>
          )}
        </Grid>
      </Card>

      <div className={classes.toolbar}>
        <div className={classes.crudButtons}>
          <Button
            style={{ minWidth: 100 }}
            size="small"
            className="text-red mr-1"
            onClick={() => navigate("/inventory")}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            Close
          </Button>
          {current_user.type !== "SU" && (
            <ExportButton
              className="mr-3"
              label="Export"
              onClick={handleExport}
            />
          )}
          {current_user.type !== "SU" && metrcEnabled && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleImportFile}
              />
              <Button
                style={{ minWidth: 100 }}
                size="small"
                className="mr-1"
                onClick={handleImportClick}
                variant="contained"
                color="primary"
                disabled={loader || saving || !inventoryMap?.length}
              >
                {saving ? "Working..." : pendingMetrc ? "Re-import" : "Import"}
              </Button>
            </>
          )}
          {current_user.type !== "SU" && metrcEnabled && pendingMetrc && (
            <>
                <Button
                  style={{ minWidth: 130 }}
                  size="small"
                  className="mr-1"
                  onClick={handleSyncToAndroid}
                  variant="contained"
                  color="primary"
                  disabled={saving}
                >
                  {saving ? "Syncing..." : "Sync to device"}
                </Button>
                {pendingMetrc && (
                  <Button
                    style={{ minWidth: 100 }}
                    size="small"
                    onClick={handleDiscardImport}
                    variant="outlined"
                    color="primary"
                    disabled={saving}
                  >
                    Discard
                  </Button>
                )}
              </>
            )}
        </div>
      </div>

      {pendingMetrc && (
        <div
          style={{
            margin: "0 20px 10px",
            padding: "8px 12px",
            background: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            borderRadius: 6,
            color: theme.palette.text.primary,
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: theme.palette.primary.main }}>
            Loaded from file
          </strong>{" "}
          — showing Metrc tags from the imported file (highlighted). Not synced
          yet; click <strong>Sync to device</strong> to save and push to the VM,
          or <strong>Discard</strong> to revert.
        </div>
      )}

      {importErrors.length > 0 && (
        <div
          style={{
            margin: "0 20px 10px",
            padding: "10px 14px",
            background: alpha(theme.palette.error.main, 0.06),
            border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
            borderRadius: 6,
            color: theme.palette.text.primary,
          }}
        >
          <strong style={{ color: theme.palette.error.main }}>
            Import validation failed
          </strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
            {importErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={classes.content}>
        <TableGenerator
          disablePagination={true}
          searchColumnsFilter={true}
          sensorTable={true}
          fields={_flatten(fields)}
          data={inventoryMap?.filter(
            (e) => e?.is_disabled === undefined || e?.is_disabled === false
          )}
          loader={loader}
          onChangePage={(page) => console.log(page)}
          // initialSort={"rack_number"}
          backendPagination={true}
          currentPage={page}
          handleSortChange={(ordering) => {
            handleSort(ordering);
          }}
          onPageChange={(page, direction) => {
            setPage(page);
          }}
          onRowPerPageChange={(rows) => {}}
          dataCount={inventoryMap?.length}
          selectedRecords={modulesSelected}
          rowOnePage={2500}
          onChangeSelected={(modulesSelected) =>
            setModulesSelected(modulesSelected)
          }
        />
      </div>
    </div>
  );
};

export default InventoryDetails;
